import { UiService } from 'src/app/services/ui.service'

// Toast-tester variants, ordered visual-first (layout sanity) then handler-specific
// (real backend payloads). Verbiage in the passthrough group is copied verbatim from
// arpio-api UnprocessableEntity raises so the preview matches production.
export function buildToastGroups(uiService: UiService): ReadonlyArray<{
  label: string
  variants: ReadonlyArray<{ label: string; fire: () => void }>
}> {
  return [
    {
      label: 'Layout & wrapping',
      variants: [
        {
          label: 'Short message',
          fire: () =>
            uiService.announceError({
              message: 'Something went wrong',
              detailedMessage: 'Please try again.',
            }),
        },
        {
          label: 'Long single-line (width clamp)',
          fire: () =>
            uiService.announceError({
              message: 'Failed to update resource',
              detailedMessage:
                'A long detailedMessage with no explicit line breaks so we can verify the toast wraps cleanly inside the fixed-width container without bleeding past the right edge of the viewport.',
            }),
        },
        {
          label: 'Multi-line (preserves \\n)',
          fire: () =>
            uiService.announceError({
              message: 'Multi-step failure',
              detailedMessage: 'Step one failed.\nStep two failed.\n\nStep three was skipped.',
            }),
        },
      ],
    },
    {
      label: 'Plain API codes (no body)',
      variants: [
        {
          label: 'Connection error (0)',
          fire: () => uiService.announceApiError({ responseCode: 0 } as any),
        },
        {
          label: 'Invalid request (400)',
          fire: () => uiService.announceApiError({ responseCode: 400 } as any),
        },
        {
          label: 'Authentication required (401)',
          fire: () => uiService.announceApiError({ responseCode: 401 } as any),
        },
        {
          label: 'Access denied (403)',
          fire: () => uiService.announceApiError({ responseCode: 403 } as any),
        },
        {
          label: 'Not found (404)',
          fire: () => uiService.announceApiError({ responseCode: 404 } as any),
        },
        {
          label: 'Conflict (409)',
          fire: () => uiService.announceApiError({ responseCode: 409 } as any),
        },
        {
          label: 'Invalid input (422)',
          fire: () => uiService.announceApiError({ responseCode: 422 } as any),
        },
        {
          label: 'Server error (500)',
          fire: () => uiService.announceApiError({ responseCode: 500 } as any),
        },
        {
          label: 'Unrecognized code (999)',
          fire: () => uiService.announceApiError({ responseCode: 999 } as any),
        },
      ],
    },
    {
      label: 'Backend message passthrough (4xx with body)',
      variants: [
        {
          label: 'Multi-app launch failure (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Application app-prod-web cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.\n\n' +
                  'Application app-prod-api cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.',
              },
            } as any),
        },
        {
          label: 'Single-app launch failure with issues list (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Application app-prod-web cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.',
              },
            } as any),
        },
        {
          // The message region takes its own scrollbar past `$eb-scroll-max`; the title, the
          // actions and the reference line stay put. One app can raise this many issues —
          // restore_api joins them all into one string.
          label: 'Single-app launch failure, long issues list (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Application app-prod-web cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.\n' +
                  '  The target region does not have a subnet matching the source subnet subnet-0a1b2c3d in availability zone us-east-1a.\n' +
                  '  The security group sg-2f8e1d44 references a peer group that does not exist in the recovery account.\n' +
                  '  The instance profile arpio-app-prod-web-role could not be assumed in the target account.\n' +
                  '  The RDS cluster prod-web-aurora has no automated backup within the selected recovery point window.\n' +
                  '  The S3 bucket app-prod-web-assets has a bucket policy that denies replication to the recovery region.\n' +
                  '  The DynamoDB table prod-web-sessions exceeds the provisioned write capacity configured for restore.\n' +
                  '  The EFS file system fs-0d91c22a is not mounted by any protected instance, so it will not be restored.',
              },
            } as any),
        },
        {
          label: 'Single-app launch failure, invalid request (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Application app-prod-web cannot be restored because the request was invalid' },
            } as any),
        },
        {
          label: 'Restore disabled for app (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Restore is disabled for application app-prod-web' },
            } as any),
        },
        {
          label: 'No applications to restore (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'No applications to restore.' },
            } as any),
        },
        {
          label: 'Account not entitled (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'This Arpio account is not entitled to restore applications.' },
            } as any),
        },
        {
          label: 'Retry only completed jobs (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Only completed jobs can be retried. Job status: IN_PROGRESS' },
            } as any),
        },
        {
          label: 'Resume recovery unavailable (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message: 'Resume recovery is not available for this application. Cause: compatibility',
              },
            } as any),
        },
        {
          label: 'RP lock acquisition failed (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Restore request failed because of the following issue: Lock for Recovery Point rp-8f21 is already held. Try again later.',
              },
            } as any),
        },
        {
          label: 'Generic backend detail (400)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 400,
              body: { message: 'Required field "applicationId" is missing from the request body.' },
            } as any),
        },
      ],
    },
    {
      label: 'Specialized substring-matched handlers',
      variants: [
        {
          label: 'Duplicate role name (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Role name already exists in account.' },
            } as any),
        },
        {
          label: 'SAML metadata expired (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'The SAML metadata does not include a valid (non-expired) EntityDescriptor with an IDPSSODescriptor.',
              },
            } as any),
        },
        {
          label: 'SAML metadata missing EntityDescriptor (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'The SAML metadata document could not be parsed or is not valid.' },
            } as any),
        },
        {
          label: 'Email verification required (403)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 403,
              body: {
                message:
                  'You must verify your email address before you can add other users to this account.  To verify your email address, click the link in the email you received when you first signed up to use this service.',
              },
            } as any),
        },
        {
          // How it really arrives: user-management's resend is a page action, so there is no
          // surface to anchor to. The retry rides along as the call site's closure, which is
          // the only way a toast can re-run work the shell cannot reach.
          label: 'Email cooldown 90s (403) — counts down, then retries',
          fire: () =>
            uiService.announceApiError(
              {
                responseCode: 403,
                body: { message: 'You must wait at least 10 seconds since the last email was sent.' },
              } as any,
              undefined,
              { onAction: () => uiService.announceCopied('Verification email sent') }
            ),
        },
        {
          // The other half of that path: what a resend looks like when it works.
          label: 'Verification email sent (success pill)',
          fire: () => uiService.announceCopied('Verification email sent'),
        },
        {
          // A 403 raised from a dialog renders in that dialog; this is the same payload with
          // nothing mounted, which is still a toast and still leads with the backend's reason.
          label: 'Delete refused, last identity provider (403)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 403,
              body: { message: 'The last identity provider in an account cannot be deleted.' },
            } as any),
        },
      ],
    },
    {
      // Stacking and pinning are shell behavior, so they can't be previewed as static blocks
      // in the Errors section — they need real toasts arriving one after another.
      label: 'Shell stack behavior',
      variants: [
        {
          label: 'Three at once (no silent replace)',
          fire: () => {
            uiService.announceApiError({ responseCode: 409 } as any)
            uiService.announceApiError({ responseCode: 404 } as any)
            uiService.announceApiError({ responseCode: 0 } as any)
          },
        },
        {
          label: 'Four at once (oldest dismissable evicted)',
          fire: () => {
            uiService.announceApiError({ responseCode: 409 } as any)
            uiService.announceApiError({ responseCode: 404 } as any)
            uiService.announceApiError({ responseCode: 0 } as any)
            uiService.announceApiError({ responseCode: 422 } as any)
          },
        },
        {
          label: 'Same error twice (collapses)',
          fire: () => {
            uiService.announceApiError({ responseCode: 0 } as any)
            uiService.announceApiError({ responseCode: 0 } as any)
          },
        },
        {
          label: 'Pinned + timed together',
          fire: () => {
            uiService.announceApiError({ responseCode: 500 } as any)
            uiService.announceCopied('Copied to clipboard!')
          },
        },
      ],
    },
    {
      label: 'Real-world examples',
      variants: [
        {
          label: 'Restore failed, recovery point missing',
          fire: () =>
            uiService.announceError({
              message: 'Recovery Failed',
              detailedMessage:
                'The recovery point you selected is no longer available. It may have been deleted or pruned by your retention policy. Refresh and try a different recovery point.',
            }),
        },
        {
          label: 'HubSpot widget unavailable',
          fire: () =>
            uiService.announceError({
              message: 'Unable to find HubSpot chat widget.',
              detailedMessage: 'Unable to find HubSpot chat widget.',
            }),
        },
      ],
    },
  ]
}
